import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * CrewMobile — /mobile crew dashboard.
 *
 * Precise digital room rendering + material-placement blueprints for
 * the field crew. Per master registry items 5, 48. Every quantity on
 * this screen comes from the granular per-item micro-engine
 * (`/api/design/micro-materials`) so what the crew sees on their phone
 * is exactly what will be delivered to the site.
 *
 * Zero manual entry on the mobile side. The crew reads. The platform
 * assembles.
 */

const CrewMobile = () => {
  const { project_id } = useParams();
  const navigate = useNavigate();

  // Property spec state (defaults for demo — normally hydrated from
  // the project record). Crew never edits these; they're set at the
  // Design Studio Specs tab by admin.
  const [specs] = useState({
    bedrooms: 2, bathrooms: 2, kitchen_sqft: 120, living_sqft: 350,
    bedroom_sqft: 150, bathroom_sqft: 60, ceiling_height: 9,
    exterior_doors: 1, interior_doors: 6, windows: 8, waste_factor: 0.10,
  });

  const [engine, setEngine] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const r = await fetch(`${BACKEND_URL}/api/design/micro-materials`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(specs),
        });
        if (r.ok) {
          const d = await r.json();
          if (!cancelled) setEngine(d);
        } else if (r.status === 401) {
          if (!cancelled) setErr("Sign in required to load the crew blueprint.");
        }
      } catch {
        if (!cancelled) setErr("Could not reach the digital platform.");
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [specs]);

  const rooms = [
    { key: "kitchen",  label: "🍳 Kitchen",   sqft: specs.kitchen_sqft,  count: 1 },
    { key: "living",   label: "🛋 Living",    sqft: specs.living_sqft,   count: 1 },
    { key: "bedroom",  label: "🛏 Bedroom",   sqft: specs.bedroom_sqft,  count: specs.bedrooms },
    { key: "bathroom", label: "🚿 Bathroom",  sqft: specs.bathroom_sqft, count: specs.bathrooms },
  ];

  return (
    <div style={page} data-testid="crew-mobile">
      <div style={header}>
        <button onClick={() => navigate("/dashboard")} style={backBtn} data-testid="crew-back">
          ← Dashboard
        </button>
        <div>
          <div style={eyebrow}>MOOVIN FORWARD LLC · CREW BLUEPRINT</div>
          <h1 style={h1}>Room Rendering + Material Placement</h1>
          <p style={sub}>
            Project <strong>{project_id || "(demo)"}</strong> · every quantity below is auto-computed from the property&apos;s dimensions. Position supplies on-site to match the blueprint. Sign off at the bottom.
          </p>
        </div>
      </div>

      <div style={main}>
        {loading && <div style={muted}>Loading blueprint from the digital platform…</div>}
        {err && <div style={errBox}>{err}</div>}

        {engine && (
          <>
            {/* ── Digital room rendering ───────────────────────── */}
            <section style={card}>
              <div style={sectionLabel}>&gt; DIGITAL ROOM RENDERING</div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, marginTop: 8 }}>
                {rooms.map(r => {
                  // Scale each room's rendered box proportionally to its
                  // sqft footprint. Aspect ratio 1.6:1 keeps the shape
                  // realistic. Larger rooms render larger — booth
                  // visitors and crew see relative scale at a glance.
                  const w = Math.sqrt(r.sqft / 1.6);
                  const l = w * 1.6;
                  const scale = Math.min(120 / Math.max(w, l), 8);
                  const boxW = Math.round(w * scale);
                  const boxH = Math.round(l * scale);
                  return (
                    <div key={r.key} style={roomCard} data-testid={`crew-room-${r.key}`}>
                      <div style={{ fontSize: 18, fontWeight: 800 }}>{r.label}</div>
                      <div style={{ color: "#7E92A8", fontSize: "0.72rem", marginTop: 4 }}>
                        {r.count} × {r.sqft} sqft · {Math.round(w)}′ × {Math.round(l)}′
                      </div>
                      <div style={{ color: "#00DC78", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.85rem", fontWeight: 800, marginTop: 4 }}>
                        Total {r.count * r.sqft} sqft
                      </div>
                      <svg
                        width="100%"
                        height="130"
                        viewBox="0 0 200 130"
                        preserveAspectRatio="xMidYMid meet"
                        style={{ marginTop: 10, display: "block" }}
                        data-testid={`crew-render-${r.key}`}
                      >
                        {/* Room outline scaled to sqft */}
                        <rect
                          x={(200 - boxW) / 2}
                          y={(130 - boxH) / 2}
                          width={boxW}
                          height={boxH}
                          fill="rgba(0,255,255,0.08)"
                          stroke="#00FFFF"
                          strokeWidth="1.5"
                          rx="2"
                        />
                        {/* Doorway indicator (top-center) */}
                        <line
                          x1="100"
                          y1={(130 - boxH) / 2}
                          x2="115"
                          y2={(130 - boxH) / 2}
                          stroke="#FFB347"
                          strokeWidth="2.5"
                        />
                        {/* Window (bottom edge) — bathroom skipped for privacy */}
                        {r.key !== "bathroom" && (
                          <line
                            x1={(200 - boxW) / 2 + boxW * 0.3}
                            y1={(130 + boxH) / 2}
                            x2={(200 - boxW) / 2 + boxW * 0.7}
                            y2={(130 + boxH) / 2}
                            stroke="#5AC8D8"
                            strokeWidth="2"
                          />
                        )}
                        {/* Room-specific fixture glyphs */}
                        {r.key === "kitchen" && (
                          <>
                            <rect x={(200 - boxW) / 2 + 4} y={(130 - boxH) / 2 + 4} width="14" height="8" fill="#A78BFA" opacity="0.7" />
                            <rect x={(200 - boxW) / 2 + boxW - 18} y={(130 - boxH) / 2 + 4} width="14" height="8" fill="#A78BFA" opacity="0.7" />
                            <circle cx={(200) / 2} cy={(130) / 2} r="4" fill="none" stroke="#A78BFA" strokeWidth="1" opacity="0.7" />
                          </>
                        )}
                        {r.key === "bathroom" && (
                          <>
                            <rect x={(200 - boxW) / 2 + 4} y={(130 + boxH) / 2 - 12} width="16" height="8" fill="#5AC8D8" opacity="0.7" rx="1" />
                            <circle cx={(200 - boxW) / 2 + boxW - 8} cy={(130 - boxH) / 2 + 8} r="3" fill="#5AC8D8" opacity="0.7" />
                          </>
                        )}
                        {r.key === "bedroom" && (
                          <rect x={(200 - boxW) / 2 + 4} y={(130 - boxH) / 2 + 4} width="18" height="14" fill="#E8B8C4" opacity="0.55" rx="1" />
                        )}
                        {r.key === "living" && (
                          <>
                            <rect x={(200 - boxW) / 2 + 4} y={(130 + boxH) / 2 - 10} width="20" height="6" fill="#E8B8C4" opacity="0.55" />
                            <rect x={(200 - boxW) / 2 + boxW - 12} y={(130 - boxH) / 2 + 4} width="8" height="10" fill="#E8B8C4" opacity="0.55" />
                          </>
                        )}
                      </svg>
                      <div style={{ display: "flex", gap: 8, marginTop: 4, fontSize: "0.6rem", color: "#7E92A8" }}>
                        <span>🟠 door</span>
                        {r.key !== "bathroom" && <span>🔵 window</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={{ marginTop: 10, color: "#B4BEC8", fontSize: "0.75rem", lineHeight: 1.55 }}>
                <strong>Property total:</strong> {engine.computed.total_floor_sqft} sqft floor · {engine.computed.total_perimeter_ft} ft perimeter · {engine.computed.wall_sqft} sqft wall · {engine.computed.trim_linear_ft} ft trim.
              </div>
            </section>

            {/* ── Material placement blueprint ─────────────────── */}
            <section style={card}>
              <div style={sectionLabel}>&gt; MATERIAL PLACEMENT · GRANULAR QUANTITIES</div>
              <div style={{ color: "#B4BEC8", fontSize: "0.75rem", marginTop: 4, marginBottom: 10 }}>
                Every line derives from the property&apos;s dimensions. The formula ships with the quantity so the owner and inspector can audit the math.
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {engine.lines.map(l => (
                  <div key={l.id} style={lineRow} data-testid={`crew-line-${l.id}`}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: "#fff", fontSize: "0.9rem", fontWeight: 800 }}>{l.label}</div>
                      <div style={{ color: "#7E92A8", fontFamily: "'JetBrains Mono', monospace", fontSize: "0.68rem", marginTop: 3, whiteSpace: "normal" }}>
                        {l.formula}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flex: "0 0 auto", marginLeft: 12 }}>
                      <div style={{ color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace", fontSize: "1.05rem", fontWeight: 800 }}>
                        {l.qty}
                      </div>
                      <div style={{ color: "#7E92A8", fontSize: "0.68rem", letterSpacing: "0.08em", textTransform: "uppercase" }}>
                        {l.unit}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section style={{ ...card, background: "rgba(0,220,120,0.06)", borderColor: "rgba(0,220,120,0.28)" }}>
              <div style={{ color: "#00DC78", fontWeight: 800, marginBottom: 6 }}>
                ✓ Blueprint locked. Position supplies to match.
              </div>
              <div style={{ color: "#B4BEC8", fontSize: "0.78rem", lineHeight: 1.55 }}>
                Every capture from your phone attaches a cryptographic handshake bound to this project + the 150-meter geofence. Photos land in the append-only immutable ledger — sign-off is verifiable at closeout.
              </div>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

// styles ─────────────────────────────────────────────────────────
const page = { minHeight: "100vh", background: "#030B1A", color: "#fff", paddingBottom: 40 };
const header = {
  padding: "20px 16px 16px",
  borderBottom: "1px solid rgba(255,255,255,0.06)",
  background: "linear-gradient(180deg, rgba(0,255,255,0.05) 0%, rgba(3,11,26,0) 100%)",
};
const backBtn = {
  background: "transparent", border: "1px solid rgba(232,184,196,0.5)",
  color: "#E8B8C4", padding: "6px 12px", borderRadius: 999,
  fontWeight: 800, fontSize: 11, letterSpacing: "0.02em", cursor: "pointer",
  marginBottom: 10,
};
const eyebrow = {
  color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem", letterSpacing: "0.24em", fontWeight: 800,
};
const h1 = { fontSize: "1.5rem", fontWeight: 900, margin: "4px 0 6px" };
const sub = { color: "#ADBED1", fontSize: "0.85rem", margin: 0, lineHeight: 1.5 };
const main = { padding: "16px 12px 24px", display: "flex", flexDirection: "column", gap: 14, maxWidth: 720, marginLeft: "auto", marginRight: "auto" };
const card = {
  background: "rgba(255,255,255,0.02)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: 12,
  padding: "14px 14px 16px",
};
const sectionLabel = {
  color: "#00FFFF", fontFamily: "'JetBrains Mono', monospace",
  fontSize: "0.66rem", letterSpacing: "0.2em", fontWeight: 800,
};
const roomCard = {
  background: "rgba(0,0,0,0.35)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 10,
  padding: "12px 12px 8px",
};
const roomPlaceholder = {
  marginTop: 10, height: 60, borderRadius: 6,
  border: "1px dashed rgba(0,255,255,0.28)",
  background: "repeating-linear-gradient(45deg, rgba(0,255,255,0.05) 0 4px, transparent 4px 8px)",
};
const lineRow = {
  display: "flex", alignItems: "center",
  padding: "10px 12px",
  background: "rgba(0,0,0,0.28)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderRadius: 8,
};
const muted = { color: "#7E92A8", fontSize: "0.85rem" };
const errBox = {
  background: "rgba(248,113,113,0.08)", color: "#F87171",
  border: "1px solid rgba(248,113,113,0.4)",
  padding: "10px 12px", borderRadius: 8, fontSize: "0.85rem",
};

export default CrewMobile;
